import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Loader2, UserPlus, Stethoscope } from 'lucide-react';
import api from '../../utils/api';
// TREATX-UAT-PHONE-BULK-MIGRATION-02 — canonical E.164 phone input.
import { PhoneInput } from '../../components/ui/phone-input';
// TREATX-PATIENT-DOCTOR-EMAIL-OTP-PRECREATE-01: shared inline OTP step.
import { EmailOtpStep } from '../../components/EmailOtpStep';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function DoctorSignup() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    full_name: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [signupComplete, setSignupComplete] = useState(false);

  // TREATX-PATIENT-DOCTOR-EMAIL-OTP-PRECREATE-01:
  //   step = 'form' → user fills the doctor signup form
  //   step = 'otp'  → user enters the 6-digit code we emailed them
  // The /api/doctors/signup endpoint is NEVER called until OTP succeeds,
  // so no rbac_users / doctor_profiles row is ever written without
  // proven email ownership.
  const [step, setStep] = useState('form');
  const [otpError, setOtpError] = useState('');
  const [otpExpiresIn, setOtpExpiresIn] = useState(600);
  const [otpResendAfter, setOtpResendAfter] = useState(60);

  const validate = () => {
    if (!form.full_name.trim() || !form.email.trim() || !form.password) {
      setError('Name, email, and password are required');
      return false;
    }
    if (form.password.length < 6) {
      setError('Password must be at least 6 characters');
      return false;
    }
    if (form.password !== form.confirmPassword) {
      setError('Passwords do not match');
      return false;
    }
    setError('');
    return true;
  };

  const requestOtp = async () => {
    const resp = await axios.post(`${API_URL}/api/auth/email-otp/request`, {
      email: form.email,
      purpose: 'doctor_signup',
      name: form.full_name,
    });
    const data = resp.data || {};
    setOtpExpiresIn(Number(data.expires_in) || 600);
    setOtpResendAfter(Number(data.resend_after) || 60);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    try {
      await requestOtp();
      setOtpError('');
      setStep('otp');
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const respMsg = err?.response?.data?.message;
      let msg = 'Could not send the verification code. Please try again.';
      if (typeof detail === 'string') msg = detail;
      else if (detail && typeof detail === 'object' && detail.message) msg = detail.message;
      else if (typeof respMsg === 'string') {
        const m = respMsg.match(/'message'\s*:\s*'([^']+)'/);
        msg = m ? m[1] : respMsg;
      }
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    setOtpError('');
    try {
      await requestOtp();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setOtpError(
        (detail && typeof detail === 'object' && detail.message) ||
        (typeof detail === 'string' ? detail : 'Resend failed. Please wait a moment and try again.')
      );
    }
  };

  // Final step: POST /api/doctors/signup with otp_code. Backend will
  // verify the OTP via EmailOtpVerificationService BEFORE creating
  // any rbac_users or doctor_profiles row.
  const handleVerifyOtpAndSignup = async (code) => {
    setLoading(true);
    setOtpError('');
    try {
      const { data } = await api.post('/doctors/signup', {
        ...form,
        otp_code: code,
      });
      if (data.success) {
        localStorage.setItem('treatx_unified_user', JSON.stringify(data.user));
        setSignupComplete(true);
      } else {
        setError(data.detail || 'Signup failed');
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      const respMsg = err?.response?.data?.message;
      const code2 = (detail && typeof detail === 'object' && detail.code)
        || (typeof respMsg === 'string' ? (respMsg.match(/'code'\s*:\s*'([a-zA-Z0-9_]+)'/) || [])[1] : null);
      const msg = (detail && typeof detail === 'object' && detail.message)
        || (typeof detail === 'string' ? detail
            : Array.isArray(detail) ? detail.map(e => e.msg || JSON.stringify(e)).join(' ') : null)
        || (typeof respMsg === 'string' ? (respMsg.match(/'message'\s*:\s*'([^']+)'/) || [])[1] : null)
        || 'Signup failed. Please try again.';
      const otpCodes = new Set([
        'otp_invalid', 'otp_expired', 'otp_not_found',
        'otp_too_many_attempts', 'otp_required', 'otp_invalid_format',
      ]);
      if (otpCodes.has(code2)) {
        setOtpError(msg);
      } else {
        setError(msg);
        setStep('form');
      }
    } finally {
      setLoading(false);
    }
  };

  if (signupComplete) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4" data-testid="doctor-signup-success">
        <Card className="w-full max-w-md shadow-lg">
          <CardContent className="pt-8 pb-6 text-center space-y-4">
            <div className="mx-auto w-16 h-16 rounded-full bg-green-50 flex items-center justify-center mb-2">
              <Stethoscope className="h-8 w-8 text-green-600" />
            </div>
            <h2 className="text-xl font-bold text-slate-900">Doctor Profile Created</h2>
            <p className="text-sm text-slate-600">
              Your personal doctor profile has been saved. To start practicing on TreatX, you need to be affiliated with a provider.
            </p>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-left">
              <p className="text-sm font-medium text-amber-800">Next Steps:</p>
              <ul className="text-sm text-amber-700 mt-2 space-y-1 list-disc list-inside">
                <li>Wait for a provider (clinic/hospital) to invite you as an affiliated doctor</li>
                <li>Or apply to register your own Doctor Practice as a provider entity</li>
              </ul>
            </div>
            <div className="flex gap-3 pt-2">
              <Button onClick={() => navigate('/doctor/profile')} className="flex-1 bg-teal-600 hover:bg-teal-700" data-testid="goto-profile-btn">
                Go to Profile
              </Button>
              <Button variant="outline" onClick={() => navigate('/provider/register')} className="flex-1" data-testid="register-practice-btn">
                Register Practice
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4" data-testid="doctor-signup-page">
      <Card className="w-full max-w-md shadow-lg">
        <CardHeader className="text-center pb-2">
          <div className="mx-auto w-12 h-12 rounded-full bg-teal-50 flex items-center justify-center mb-3">
            <Stethoscope className="h-6 w-6 text-teal-600" />
          </div>
          <CardTitle className="text-xl font-bold text-slate-900">Doctor Registration</CardTitle>
          <p className="text-sm text-slate-500 mt-1">Create your personal doctor profile on TreatX. This is a person-level registration, not a provider entity.</p>
        </CardHeader>
        <CardContent>
          {step === 'otp' ? (
            <EmailOtpStep
              email={form.email}
              cooldownSeconds={otpResendAfter}
              expiresInSeconds={otpExpiresIn}
              loading={loading}
              errorMsg={otpError}
              onVerify={handleVerifyOtpAndSignup}
              onResend={handleResendOtp}
              onBack={() => { setStep('form'); setOtpError(''); }}
              testIdPrefix="doctor-otp"
            />
          ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="full_name">Full Name *</Label>
              <Input
                id="full_name"
                value={form.full_name}
                onChange={(e) => setForm(f => ({ ...f, full_name: e.target.value }))}
                placeholder="Dr. John Smith"
                data-testid="doctor-signup-name"
              />
            </div>
            <div>
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                value={form.email}
                onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))}
                placeholder="doctor@example.com"
                data-testid="doctor-signup-email"
              />
            </div>
            <div>
              <Label htmlFor="password">Password *</Label>
              <Input
                id="password"
                type="password"
                value={form.password}
                onChange={(e) => setForm(f => ({ ...f, password: e.target.value }))}
                placeholder="Min 6 characters"
                data-testid="doctor-signup-password"
              />
            </div>
            <div>
              <Label htmlFor="confirmPassword">Confirm Password *</Label>
              <Input
                id="confirmPassword"
                type="password"
                value={form.confirmPassword}
                onChange={(e) => setForm(f => ({ ...f, confirmPassword: e.target.value }))}
                placeholder="Re-enter password"
                data-testid="doctor-signup-confirm-password"
              />
            </div>
            <div>
              <Label htmlFor="phone">Phone (optional)</Label>
              <PhoneInput
                value={form.phone}
                onChange={(e164) => setForm(f => ({ ...f, phone: e164 }))}
                testId="doctor-signup-phone"
                placeholder="Phone number"
              />
            </div>

            {error && (
              <p className="text-sm text-red-500" data-testid="doctor-signup-error">{error}</p>
            )}

            <Button type="submit" className="w-full bg-teal-600 hover:bg-teal-700" disabled={loading} data-testid="doctor-signup-submit">
              {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <UserPlus className="h-4 w-4 mr-2" />}
              Continue
            </Button>

            <p className="text-center text-xs text-slate-400 mt-3">
              Already have an account? <Link to="/doctor/login" className="text-teal-600 hover:underline">Log in</Link>
            </p>
          </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
